// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * React hook for managing asset filter states in both API and UI contexts.
 * This module provides functionality to transform and combine filter states from
 * URL parameters and local storage defaults into formats suitable for both
 * API requests and UI components.
 */
import {
  AssetFilter,
  AssetSortProperty,
  assetSortPropertySchema,
} from '@/asset/api/get-assets';
import {
  AssetAssignmentStatus,
  assetAssignmentStatusSchema,
} from '@/constants/asset-assignment-status';
import {
  AssetFunctionalStatus,
  assetFunctionalStatusFilterSchema,
} from '@/constants/asset-functional-status';
import {
  AssetInventoryStatus,
  assetInventoryStatusSchema,
} from '@/constants/asset-inventory-status';
import {
  AssetTransitStatus,
  assetTransitStatusSchema,
} from '@/constants/asset-transit-status';
import {
  LocationCompanyType,
  locationCompanyTypeSchema,
} from '@/constants/location-company-type';
import { Sort, sortDirectionSchema } from '@/constants/sort';
import { APP_ALLOWED_CURRENCIES } from '@/currency/constants/iso';
import { searchTermSchema } from '@/utilities/search-term';
import { z } from 'zod';

/**
 * Schema for ownership values in filter state.
 */
const ownershipValueSchema = z.enum(['manufacturer', 'customer']);

export type OwnershipValue = z.infer<typeof ownershipValueSchema>;

/**
 * Schema for URL search parameters that affect filtering.
 */
const assetSearchSchema = z.object({
  query: searchTermSchema,
  scrapped: z.boolean().optional(),
  products: z.array(z.string()).optional(),
  locations: z.array(z.string()).optional(),
  customers: z.array(z.string()).optional(),
  ownership: z.array(ownershipValueSchema).optional().catch([]),
  assignment: z.array(assetAssignmentStatusSchema).optional().catch([]),
  transitStatus: z.array(assetTransitStatusSchema).optional().catch([]),
  inventoryStatus: z.array(assetInventoryStatusSchema).optional().catch([]),
  customerLocations: z.array(z.string()).optional(),
  functionalStatuses: z
    .array(assetFunctionalStatusFilterSchema)
    .optional()
    .catch([]),
  locationCompanyTypes: z.array(locationCompanyTypeSchema).optional().catch([]),
  // Deep-linked from the accounting pages. There is no visible control for it, so an
  // unrecognized code falls back to no filter rather than failing the route.
  currency: z.enum(APP_ALLOWED_CURRENCIES).optional().catch(undefined),
});

export const assetSearchListingSchema = assetSearchSchema.extend({
  sort: assetSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  move: z.boolean().optional().catch(false),
  page: z.number().optional(),
  limit: z.number().optional(),
});

export type AssetSearchListing = z.infer<typeof assetSearchListingSchema>;

/**
 * Schema for filter preferences stored in local storage.
 * Note: inventoryStatus and transitStatus are excluded as they are controlled
 * by the toggle buttons and should not be saved as default filters.
 */
export const filterSchema = z.object({
  ownership: z.array(ownershipValueSchema).optional(),
  assignment: z.array(assetAssignmentStatusSchema).optional(),
  productIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  customerIds: z.array(z.string()).optional(),
  customerLocations: z.array(z.string()).optional(),
  functionalStatuses: z.array(assetFunctionalStatusFilterSchema).optional(),
  locationCompanyTypes: z.array(locationCompanyTypeSchema).optional(),
  scrapped: z.boolean().optional(),
});

/**
 * UI-specific filter state with non-null arrays.
 */
interface UIFilters {
  assignment: AssetAssignmentStatus[];
  ownership: OwnershipValue[];
  productIds: string[];
  locationIds: string[];
  customerIds: string[];
  transitStatus: AssetTransitStatus[];
  inventoryStatus: AssetInventoryStatus[];
  customerLocations: string[];
  functionalStatuses: AssetFunctionalStatus[];
  locationCompanyTypes: LocationCompanyType[];
  scrapped: boolean;
  pageNumber: number;
  pageSize: number;
}

/**
 * A React hook that manages both API and UI filter states.
 * Combines URL search parameters with local storage defaults to create
 * consistent filter states for both API requests and UI components.
 *
 * @param searchParameters - Current URL search parameters
 * @param defaultFilters - Filter preferences from local storage
 * @param sort
 * @returns Object containing both API and UI filter states
 * @property {AssetFilter} apiFilter - Filter state formatted for API requests
 * @property {UIFilters} uiFilters - Filter state formatted for UI components with non-null arrays
 */
export function useAssetFilters(
  searchParameters: AssetSearchListing,
  defaultFilters: z.infer<typeof filterSchema> | null,
  sort?: Sort<AssetSortProperty>
) {
  const hasFilterQueryParams =
    searchParameters.products !== undefined ||
    searchParameters.locations !== undefined ||
    searchParameters.customers !== undefined ||
    searchParameters.ownership !== undefined ||
    searchParameters.assignment !== undefined ||
    searchParameters.transitStatus !== undefined ||
    searchParameters.inventoryStatus !== undefined ||
    searchParameters.customerLocations !== undefined ||
    searchParameters.functionalStatuses !== undefined ||
    searchParameters.locationCompanyTypes !== undefined ||
    searchParameters.scrapped !== undefined ||
    searchParameters.currency !== undefined;

  const filtersToUse = hasFilterQueryParams ? null : defaultFilters;

  // API filter state
  // Note: transitStatus and inventoryStatus only come from URL params (toggle buttons)
  const apiFilter: AssetFilter = {
    pageNumber: searchParameters.page ?? 1,
    pageSize: searchParameters.limit ?? 50,
    query: searchParameters.query,
    ownership: searchParameters.ownership ?? filtersToUse?.ownership,
    assignment: searchParameters.assignment ?? filtersToUse?.assignment,
    productIds: searchParameters.products ?? filtersToUse?.productIds,
    locationIds: searchParameters.locations ?? filtersToUse?.locationIds,
    customerIds: searchParameters.customers ?? filtersToUse?.customerIds,
    transitStatus: searchParameters.transitStatus,
    inventoryStatus: searchParameters.inventoryStatus,
    customerLocations:
      searchParameters.customerLocations ?? filtersToUse?.customerLocations,
    functionalStatuses:
      searchParameters.functionalStatuses ?? filtersToUse?.functionalStatuses,
    locationCompanyTypes:
      searchParameters.locationCompanyTypes ??
      filtersToUse?.locationCompanyTypes,
    scrapped:
      (searchParameters.scrapped ?? filtersToUse?.scrapped) ? 'all' : undefined,
    currency: searchParameters.currency,
    sort: sort,
  };

  // UI filter state with empty array fallbacks
  // Note: transitStatus and inventoryStatus only come from URL params (toggle buttons)
  const uiFilters: UIFilters = {
    ownership: searchParameters.ownership ?? filtersToUse?.ownership ?? [],
    assignment: searchParameters.assignment ?? filtersToUse?.assignment ?? [],
    productIds: searchParameters.products ?? filtersToUse?.productIds ?? [],
    locationIds: searchParameters.locations ?? filtersToUse?.locationIds ?? [],
    customerIds: searchParameters.customers ?? filtersToUse?.customerIds ?? [],
    transitStatus: searchParameters.transitStatus ?? [],
    inventoryStatus: searchParameters.inventoryStatus ?? [],
    customerLocations:
      searchParameters.customerLocations ??
      filtersToUse?.customerLocations ??
      [],
    functionalStatuses:
      searchParameters.functionalStatuses ??
      filtersToUse?.functionalStatuses ??
      [],
    locationCompanyTypes:
      searchParameters.locationCompanyTypes ??
      filtersToUse?.locationCompanyTypes ??
      [],
    scrapped: searchParameters.scrapped ?? filtersToUse?.scrapped ?? false,
    pageNumber: searchParameters.page ?? 1,
    pageSize: searchParameters.limit ?? 50,
  };

  return {
    apiFilter,
    uiFilters,
  };
}
